import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right.js";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check.js";
import Sparkles from "lucide-react/dist/esm/icons/sparkles.js";
import Building2 from "lucide-react/dist/esm/icons/building-2.js";
import Bug from "lucide-react/dist/esm/icons/bug.js";
import Trees from "lucide-react/dist/esm/icons/trees.js";
import Camera from "lucide-react/dist/esm/icons/camera.js";
import Truck from "lucide-react/dist/esm/icons/truck.js";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2.js";
import Award from "lucide-react/dist/esm/icons/award.js";
import Clock from "lucide-react/dist/esm/icons/clock.js";
import Users from "lucide-react/dist/esm/icons/users.js";
import MapPin from "lucide-react/dist/esm/icons/map-pin.js";
import FileText from "lucide-react/dist/esm/icons/file-text.js";
import Phone from "lucide-react/dist/esm/icons/phone.js";
import Search from "lucide-react/dist/esm/icons/search.js";
import ClipboardCheck from "lucide-react/dist/esm/icons/clipboard-check.js";
import Handshake from "lucide-react/dist/esm/icons/handshake.js";
import HelpCircle from "lucide-react/dist/esm/icons/help-circle.js";
import { Layout } from "@/components/site/Layout";
import { Seo } from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { useI18n, localized, pick } from "@/lib/i18n";
import { useLp } from "@/lib/localePath";
import { fetchPage, fetchServices, fetchSettings } from "@/lib/queries";
import { resolveImage, banners, bannerSrcSets, heroSlides, heroSlideSrcSets } from "@/lib/images";
import { useEffect, useState } from "react";
import { useDeferredReady } from "@/hooks/use-deferred-ready";
import { fallbackServices, mergeServiceFallbacks, servicePath } from "@/lib/serviceData";
import aboutImage from "@/assets/about-widget.webp";
import { getUseCases, USE_CASES_LABEL } from "@/lib/useCases";
import { COPY } from "@/lib/uiCopy";
import { getHomeFAQs, faqJsonLd } from "@/lib/faqData";
import { itemListJsonLd, ORGANIZATION_ID, WEBSITE_ID } from "@/lib/seoSchemas";

const iconMap: Record<string, any> = {
  Shield: ShieldCheck, Sparkles, Building2, Bug, Trees, Camera, Truck,
};

export default function Index() {
  const { t, locale } = useI18n();
  const lp = useLp();
  const [heroIndex, setHeroIndex] = useState(0);
  const animReady = useDeferredReady();
  const dataReady = useDeferredReady(1500);
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: fetchServices, enabled: dataReady });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchSettings, enabled: dataReady });
  const { data: about } = useQuery({ queryKey: ["page", "felsefemiz"], queryFn: () => fetchPage("felsefemiz"), enabled: dataReady });
  useEffect(() => {
    if (!animReady) return;
    const id = setInterval(() => setHeroIndex((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(id);
  }, [animReady]);

  // Sadece geçerli dilde değer dön; eksikse boş dön ki UI çevirili yedeğe (COPY) düşsün.
  // TR dışı dillerde value_tr'ye düşmek "karışık dil" görünümüne sebep oluyordu.
  const get = (k: string) => {
    const s = settings?.get(k);
    if (!s) return "";
    const v = s[`value_${locale}`];
    if (v) return v;
    if (locale === "tr") return s.value_en || "";
    return "";
  };

  return (
    <Layout>
      {(() => {
        const list = mergeServiceFallbacks((services && services.length > 0) ? services : fallbackServices);
        const faqs = getHomeFAQs(locale);
        return (
          <Seo
            path="/"
            /* SEO başlığı hero sloganından AYRI tutulur: slogan kullanıcıya, başlık arama
               sonucuna hitap eder. Önceden hero_title ("Güvenli Alanlar, Huzurlu Yarınlar")
               başlık olarak basılıyordu → "anadolu yakası güvenlik firması" sorgusunda
               ana sayfa 9. sıradayken 0 tık alıyordu (GSC, Haz-Tem 2026). */
            title={pick(locale, COPY.homeSeoTitle)}
            description={pick(locale, {
              tr: "2004'ten bu yana Anadolu Yakası'nda özel güvenlik, temizlik ve tesis yönetimi. 5188 belgeli personel, 7/24 izleme merkezi. Ücretsiz keşif: 0216 222 11 44.",
              en: "Private security, cleaning and facility management on Istanbul's Anatolian Side since 2004. Licensed officers under Law 5188, in-house 24/7 monitoring centre. Free site survey: +90 216 222 11 44.",
              de: "Seit 2004 Sicherheitsdienste, Reinigung und Facility Management auf der anatolischen Seite Istanbuls. Lizenziertes Personal nach Gesetz 5188, eigene 24/7-Leitstelle. Kostenlose Besichtigung: +90 216 222 11 44.",
              es: "Desde 2004: seguridad privada, limpieza y gestión de instalaciones en el lado anatolio de Estambul. Personal habilitado (Ley 5188) y central de monitoreo 24/7 propia. Visita gratuita: +90 216 222 11 44.",
              ru: "С 2004 года — охрана, клининг и управление объектами на азиатской стороне Стамбула. Лицензированный персонал (закон 5188), собственный центр мониторинга 24/7. Бесплатный выезд: +90 216 222 11 44.",
              fr: "Depuis 2004 : sécurité privée, nettoyage et facility management sur la rive anatolienne d'Istanbul. Personnel agréé (loi 5188), centre de surveillance 24/7 interne. Visite gratuite : +90 216 222 11 44.",
              it: "Dal 2004: sicurezza privata, pulizie e facility management nella parte anatolica di Istanbul. Personale abilitato (Legge 5188), centrale di monitoraggio 24/7 interna. Sopralluogo gratuito: +90 216 222 11 44.",
            } as any)}
            jsonLd={[
              {
                // Not: tam Organization/ProfessionalService düğümü index.html şablonunda
                // (sameAs/geo/çalışma saatleri dahil) zaten statik olarak var, sadece ana
                // sayfada kalıyor (prerender.mjs stripHomeOnlyJsonLd). Burada onu TEKRAR
                // eklemek eksik bir kopya yaratır — sadece @id ile referans veriyoruz.
                "@context": "https://schema.org",
                "@type": "WebSite",
                "@id": WEBSITE_ID,
                name: "Anadolu Terra",
                url: "https://anadoluterra.com/",
                inLanguage: locale,
                publisher: { "@id": ORGANIZATION_ID },
              },
              itemListJsonLd(list.map((s: any) => ({ slug: s.slug, name: localized(s, "title", locale) }))),
              faqJsonLd(faqs),
            ]}
          />

        );
      })()}
      {/* HERO */}
      <section className="relative min-h-[640px] flex items-center overflow-hidden bg-ink text-primary-foreground">
        <div className="absolute inset-0">
          {heroSlides.map((src, i) => {
            const heroAlts = [
              "Anadolu Terra 7/24 guvenlik izleme merkezi — elektronik guvenlik hizmetleri",
              "Anadolu Terra ozel guvenlik personeli gorev basinda — ozel guvenlik hizmetleri",
              "Anadolu Terra guvenlik ekibi saha hizmeti — koruma ve guvenlik hizmetleri",
              "Anadolu Terra temizlik ekibi gorev basinda — genel temizlik hizmetleri",
            ];
            if (i !== 0 && !animReady) return null;
            return (
              <img
                key={src}
                src={i === 0 ? "/hero-slide-1-monitoring.webp" : src}
                {...(i === 0
                  ? { srcSet: "/hero-slide-1-monitoring-640.webp 640w, /hero-slide-1-monitoring-768.webp 768w, /hero-slide-1-monitoring.webp 1199w", sizes: "100vw" }
                  : heroSlideSrcSets[i]
                    ? { srcSet: heroSlideSrcSets[i], sizes: "100vw" }
                    : {})}
                alt={heroAlts[i] || "Anadolu Terra hizmetleri"}
                width={1920}
                height={1080}
                decoding="async"
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "low"}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ease-in-out ${i === heroIndex ? `opacity-100 ${animReady ? "animate-ken-burns-wide" : ""}` : "opacity-0"}`}
              />





            );
          })}
          {/* Daha şeffaf overlay: arkadaki resim net görünsün, metin okunabilir kalsın */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/70 via-primary/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
          {/* Yumuşak ışık halesi */}
          {animReady && (
            <>
              <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -left-24 h-[36rem] w-[36rem] rounded-full blur-3xl animate-float-glow"
                style={{ background: "radial-gradient(closest-side, hsl(var(--accent) / 0.45), transparent 70%)" }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 right-[-6rem] h-[28rem] w-[28rem] rounded-full blur-3xl animate-float-glow"
                style={{ background: "radial-gradient(closest-side, hsl(var(--primary-glow) / 0.35), transparent 70%)", animationDelay: "2s" }}
              />
            </>
          )}
        </div>
        <div className="container-page relative py-20 md:py-28 min-h-[560px] md:min-h-[640px]">
          <div className="max-w-3xl">
            <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-4 py-1.5 text-[10px] md:text-xs font-semibold tracking-[0.14em] md:tracking-[0.18em] uppercase leading-relaxed text-primary-foreground/90 backdrop-blur-sm">
              <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              {pick(locale, COPY.heroBadge)}
            </span>
            <h1 className="mt-4 font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] drop-shadow-lg">
              {(() => {
                const full = get("hero_title") || pick(locale, COPY.heroTitleFallback);
                const parts = full.split(/,\s*/);
                const line1 = parts[0] || full;
                const line2 = parts.slice(1).join(", ");
                return (
                  <>
                    <span className={`block ${animReady ? "text-shimmer" : ""}`}>{line1}{line2 ? "," : ""}{line2 ? " " : ""}</span>
                    {line2 && <span className={`block ${animReady ? "text-shimmer" : ""}`}>{line2}</span>}

                  </>
                );
              })()}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/85 max-w-2xl leading-relaxed text-balance">
              {get("hero_subtitle") || pick(locale, COPY.heroDescFallback)}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent hover-scale">
                <Link to={lp("/hizmetlerimiz")}>
                  {t("hero.cta")} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10">
                <Link to={lp("/iletisim") + "#form"}>{t("hero.contact")}</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Slide göstergeleri */}
        <div className="absolute bottom-8 right-6 md:right-10 z-10 flex gap-2">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => setHeroIndex(i)}
              className={`p-2 rounded-full transition-all duration-300 ${i === heroIndex ? "w-8 bg-primary-foreground" : "w-3 bg-primary-foreground/40 hover:bg-primary-foreground/70"}`}
            />
          ))}
        </div>
        {/* Aşağı kaydır göstergesi */}
        <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-primary-foreground/70 animate-scroll-bounce">
          <div className="h-9 w-5 rounded-full border border-primary-foreground/40 flex items-start justify-center p-1">
            <span className="block h-1.5 w-1 rounded-full bg-primary-foreground/80" />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-border bg-secondary/40">
        <div className="container-page grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[
            { num: new Date().getFullYear() - 2004 + "+", label: t("stats.years") },
            { num: "6", label: t("stats.services") },
            { num: "500+", label: t("stats.clients") },
            { num: "1000+", label: t("stats.team") },
          ].map((s, i) => (
            <div key={i} className="px-4 py-8 md:py-10 text-center">
              <div className="font-display text-3xl md:text-4xl font-bold text-primary">{s.num}</div>
              <div className="mt-1 text-xs md:text-sm uppercase tracking-wider text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="section-padding">
        <div className="container-page grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <div className="overflow-hidden rounded-2xl md:overflow-visible">
              <img src={aboutImage} alt="Anadolu Terra A.S. — ozel guvenlik, zirhli arac tahsisi ve tesis yonetimi hizmetleri" loading="lazy" decoding="async" width={800} height={600} className="rounded-2xl shadow-elevated w-full aspect-square md:aspect-[4/3] object-cover object-center scale-110 md:scale-100 origin-center" />
            </div>
            <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-accent text-accent-foreground rounded-xl p-4 md:p-6 shadow-elevated max-w-[180px] md:max-w-[220px]">
              <div className="font-display text-2xl md:text-3xl font-bold">{new Date().getFullYear() - 2004}+</div>
              <div className="text-xs md:text-sm font-medium mt-1">{t("stats.years")}</div>
            </div>
          </div>

          <div>
            <span className="heading-eyebrow">{t("section.about.eyebrow")}</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary heading-divider">
              {pick(locale, COPY.aboutWhoTitle)}
            </h2>
            <div className="mt-8 space-y-4 text-muted-foreground leading-relaxed">
              {(() => {
                const ownContent = about ? (about as any)[`content_${locale}`] : "";
                const fallback = pick(locale, COPY.aboutFallback);
                const content = about ? localized(about, "content", locale) : "";
                // If the CMS has no content for this locale, use the in-app fallback (translated).
                const source = (ownContent && ownContent.trim().length > 0) ? ownContent : (fallback || content);
                const paragraphs = source.split("\n\n").filter((p) => p.trim() && !p.startsWith("##")).slice(0, 3);
                return paragraphs.map((p, i) => (
                  <p key={i} className={i >= 2 ? "hidden md:block" : ""}>{p}</p>
                ));
              })()}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <ul className="grid grid-cols-2 gap-x-8 gap-y-2">
                {[COPY.aboutFeature1, COPY.aboutFeature2, COPY.aboutFeature3, COPY.aboutFeature4].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                    <CheckCircle2 className="h-4 w-4 text-accent" /> {pick(locale, item)}
                  </li>
                ))}
              </ul>
              <Button asChild size="sm" className="w-full sm:w-auto sm:ml-auto md:mr-12 lg:mr-20 bg-primary hover:bg-primary/90 text-xs px-4 py-2 h-9 justify-center">
                <Link to={lp("/felsefemiz")}>{t("common.readMore")} <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US — feature strip */}
      <section className="section-padding pt-4 md:pt-6">
        <div className="container-page">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="heading-eyebrow">{pick(locale, COPY.whyEyebrow)}</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary">
              {pick(locale, COPY.whyTitle)}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {pick(locale, COPY.whySub)}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { Icon: Award, title: COPY.feat1Title, desc: COPY.feat1Desc },
              { Icon: Clock, title: COPY.feat2Title, desc: COPY.feat2Desc },
              { Icon: Users, title: COPY.feat3Title, desc: COPY.feat3Desc },
              { Icon: MapPin, title: COPY.feat4Title, desc: COPY.feat4Desc },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="group relative rounded-2xl border border-border bg-card p-6 shadow-card hover:shadow-elevated hover:-translate-y-1 transition-smooth">
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-accent text-accent-foreground shadow-accent shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-lg font-semibold text-primary">{pick(locale, title)}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{pick(locale, desc)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — use cases */}
      <section className="section-padding pt-8 md:pt-12 bg-secondary/40">
        <div className="container-page">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="heading-eyebrow">{t("section.services.eyebrow")}</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary">{t("section.services.title")}</h2>
            <p className="mt-4 text-muted-foreground">{t("section.services.subtitle")}</p>
          </div>
          {(() => {
            const list = mergeServiceFallbacks((services && services.length > 0) ? services : fallbackServices);
            return (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {list.map((s: any) => {
                  const Icon = iconMap[s.icon || ""] ?? ShieldCheck;
                  const cases = getUseCases(s.slug, locale);
                  return (
                    <Link
                      key={s.id}
                      to={lp(servicePath(s.slug))}
                      className="group relative flex flex-col rounded-2xl bg-card border border-border shadow-card hover:shadow-elevated hover:-translate-y-1 transition-smooth overflow-hidden"
                    >
                      {/* top accent bar */}
                      <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary" />
                      <div className="p-6 flex flex-col h-full">
                        <div className="flex items-start gap-4">
                          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-accent text-accent-foreground shadow-accent shrink-0">
                            <Icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-display text-lg font-semibold text-primary group-hover:text-accent transition-base">
                              {localized(s, "title", locale)}
                            </h3>
                            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{localized(s, "summary", locale)}</p>
                          </div>
                        </div>

                        <div className="mt-5 pt-5 border-t border-border">
                          <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-accent">
                            {USE_CASES_LABEL[locale]}
                          </span>
                          <ul className="mt-3 space-y-2">
                            {cases.map((c) => (
                              <li key={c} className="flex items-start gap-2 text-sm text-foreground/85">
                                <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                                <span>{c}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-accent transition-base">
                          {t("common.examine")} <ArrowRight className="h-4 w-4 transition-base group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>
      {/* SLOGAN DIVIDER — desktop only */}
      <section className="hidden md:block bg-background">
        <div className="container-page">
          <div className="relative flex items-center justify-center py-10">
            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            <div className="relative flex items-center gap-5 bg-background px-8">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              <p className="font-display text-base lg:text-lg italic font-light tracking-wide text-primary/80 text-center">
                {pick(locale, COPY.slogan)}
              </p>
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS — 4 steps */}
      <section className="section-padding bg-secondary/40">
        <div className="container-page">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="heading-eyebrow">{pick(locale, COPY.processEyebrow)}</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary">
              {pick(locale, COPY.processTitle)}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Phone, title: COPY.step1Title, desc: COPY.step1Desc },
              { Icon: Search, title: COPY.step2Title, desc: COPY.step2Desc },
              { Icon: ClipboardCheck, title: COPY.step3Title, desc: COPY.step3Desc },
              { Icon: Handshake, title: COPY.step4Title, desc: COPY.step4Desc },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="rounded-2xl bg-card border border-border p-6 shadow-card hover:shadow-elevated transition-smooth">
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-lg font-semibold text-primary">{pick(locale, title)}</h3>
                      <span aria-hidden="true" className="font-display text-3xl font-bold text-accent/60 leading-none">0{i + 1}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{pick(locale, desc)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS — sectors served */}
      <section className="section-padding bg-ink text-ink-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={banners[1]} srcSet={bannerSrcSets[1]} sizes="100vw" alt="" loading="lazy" decoding="async" width={1440} height={810} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        </div>
        <div className="container-page relative">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:sticky lg:top-28">
              <span className="heading-eyebrow text-accent">{pick(locale, COPY.areasEyebrow)}</span>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold">
                {pick(locale, COPY.areasTitle)}
              </h2>
              <p className="mt-4 text-ink-foreground/70">
                {pick(locale, COPY.areasSub)}
              </p>
              <Button asChild className="mt-8 bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent">
                <Link to={lp("/hizmet-alanlarimiz")}>
                  {pick(locale, COPY.areasDiscover)} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
              {[COPY.sector1, COPY.sector2, COPY.sector3, COPY.sector4, COPY.sector5, COPY.sector6, COPY.sector7, COPY.sector8].map((a, i) => (
                <div key={i} className="group flex items-center gap-3 rounded-xl border border-ink-foreground/10 bg-ink-foreground/[0.04] px-4 py-4 hover:border-accent/60 hover:bg-ink-foreground/[0.08] transition-base">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-base">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-ink-foreground/90">{pick(locale, a)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATALOG STRIP */}
      <section className="py-12 bg-gradient-light border-y border-border">
        <div className="container-page flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-accent shrink-0">
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-primary">
                {pick(locale, COPY.catalogTitle)}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {pick(locale, COPY.catalogDesc)}
              </p>
            </div>
          </div>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <a href="https://anadoluterra.com/Content/Upload/Dosyalar/katalog.pdf" target="_blank" rel="noopener noreferrer">
              <FileText className="mr-2 h-4 w-4" />
              {pick(locale, COPY.catalogDownload)}
            </a>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background">
        <div className="container-page max-w-4xl">
          <div className="text-center mb-12">
            <span className="heading-eyebrow inline-flex items-center gap-2"><HelpCircle className="h-4 w-4 text-accent" />{pick(locale, COPY.faqEyebrow)}</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary">
              {pick(locale, COPY.faqTitle)}
            </h2>
          </div>
          <div className="space-y-3">
            {getHomeFAQs(locale).map((f, i) => (
              <details key={i} className="group rounded-xl border border-border bg-card p-5 shadow-card open:shadow-elevated transition-smooth">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-display text-base md:text-lg font-semibold text-primary">
                  <span>{f.q}</span>
                  <span className="text-accent text-2xl leading-none transition-transform group-open:rotate-45 shrink-0">+</span>
                </summary>
                <p className="mt-3 text-sm md:text-base text-foreground/80 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-hero text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={banners[1]} srcSet={bannerSrcSets[1]} sizes="100vw" alt="" loading="lazy" decoding="async" width={1440} height={810} className="h-full w-full object-cover" />
        </div>
        <div className="container-page relative text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold">{t("section.cta.title")}</h2>
          <p className="mt-4 text-primary-foreground/80">{t("section.cta.subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent">
              <Link to={lp("/teklif-iste")}>{t("common.requestQuote")}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 bg-transparent hover:bg-primary-foreground/10 text-primary-foreground">
              <Link to={lp("/iletisim")}>{t("common.contactUs")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
